import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdfAPI } from '../utils/api';
import { CustomContent } from '../types';

const EditPage: React.FC = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState<CustomContent>({
    title: '',
    description: '',
    sectionTitle: 'OUR FOCUS, YOUR DIAGNOSIS.',
    page2Content: {
      section1: {
        title: 'LEADERS IN DERMATOPATHOLOGY',
        description: 'We pride ourselves on our team of board-certified doctors who have extensive training in dermatology and dermatopathology',
        enabled: true
      },
      section2: {
        title: 'COMPLETELY PHYSICIAN-OWNED',
        description: 'We\'re not a publicly traded company. No outside investors.',
        enabled: true
      },
      section3: {
        title: 'DEDICATED IN-HOUSE BILLING',
        description: 'Same support team year-round for patients and providers alike.',
        enabled: true
      },
      section4: {
        title: 'COMMITTED COURIER SERVICE',
        description: 'Know who\'s servicing your location everyday.',
        enabled: true
      },
      section5: {
        title: 'RAPID TURN-AROUND-TIME',
        description: 'Comprehensive testing (including DIF studies) performed in-house for quick results.',
        enabled: true
      }
    }
  });
  const [activeTab, setActiveTab] = useState<'page1' | 'page2'>('page1');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplateContent();
  }, []);

  const fetchTemplateContent = async () => {
    try {
      setLoading(true);
      // Since this is the deprecated EditPage, default to dermatology service line
      const templateContent = await pdfAPI.getTemplateContent('dermatology');
      setContent(prev => ({
        ...prev,
        ...templateContent
      }));
    } catch (err) {
      setError('Failed to load template content');
      console.error('Error fetching template content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CustomContent, value: string) => {
    setContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePage2InputChange = (section: keyof NonNullable<CustomContent['page2Content']>, field: 'title' | 'description', value: string) => {
    setContent(prev => ({
      ...prev,
      page2Content: {
        ...prev.page2Content!,
        [section]: {
          ...prev.page2Content![section],
          [field]: value
        }
      }
    }));
  };

  const handlePage2ToggleEnabled = (section: keyof NonNullable<CustomContent['page2Content']>) => {
    setContent(prev => ({
      ...prev,
      page2Content: {
        ...prev.page2Content!,
        [section]: {
          ...prev.page2Content![section],
          enabled: !prev.page2Content![section].enabled
        }
      }
    }));
  };

  const getEnabledSectionsCount = () => {
    if (!content.page2Content) return 0;
    return Object.values(content.page2Content).filter(section => section.enabled).length;
  };

  const handleSave = async () => {
    if (!content.title?.trim() || !content.description?.trim() || !content.sectionTitle?.trim()) {
      setError('Page 1 title, description, and section title are required');
      return;
    }

    const enabledCount = getEnabledSectionsCount();
    if (enabledCount === 0) {
      setError('At least one section must be enabled on Page 2');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      // Since this is the deprecated EditPage, default to dermatology service line
      await pdfAPI.updateTemplateContent(content, 'dermatology');
      setMessage('Template content updated successfully for Dermatology service line!');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError('Failed to update template content');
      console.error('Error updating template content:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchTemplateContent();
    setError(null);
    setMessage(null);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const renderPage1Editor = () => (
    <>
      {/* Title Section */}
      <div className="mb-4">
        <label htmlFor="title" className="form-label">
          <strong>Page Title</strong>
        </label>
        <div className="mb-2">
          <small className="text-muted">
            This appears as the main title on the first page of the PDF. 
            Example: "DERMATOPATHOLOGISTS", "PODIATRISTS", etc.
          </small>
        </div>
        <input
          type="text"
          className="form-control"
          id="title"
          value={content.title || ''}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Enter the title text..."
          maxLength={50}
        />
        <div className="form-text">
          {content.title?.length || 0}/50 characters
        </div>
      </div>

      {/* Section Title */}
      <div className="mb-4">
        <label htmlFor="sectionTitle" className="form-label">
          <strong>Section Title</strong>
        </label>
        <div className="mb-2">
          <small className="text-muted">
            This is the title for the "Our Focus, Your Diagnosis" section that appears on both pages.
            Keep it short and impactful.
          </small>
        </div>
        <input
          type="text"
          className="form-control"
          id="sectionTitle"
          value={content.sectionTitle || ''}
          onChange={(e) => handleInputChange('sectionTitle', e.target.value)}
          placeholder="Enter the section title..."
          maxLength={100}
        />
        <div className="form-text">
          {content.sectionTitle?.length || 0}/100 characters
        </div>
      </div>

      {/* Description Section */}
      <div className="mb-4">
        <label htmlFor="description" className="form-label">
          <strong>Description Paragraph</strong>
        </label>
        <div className="mb-2">
          <small className="text-muted">
            This appears below the section title. 
            Describe Sagis's commitment to quality diagnostic services.
          </small>
        </div>
        <textarea
          className="form-control"
          id="description"
          rows={5}
          value={content.description || ''}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter the description paragraph..."
          maxLength={500}
        />
        <div className="form-text">
          {content.description?.length || 0}/500 characters
        </div>
      </div>

      {/* Preview Section */}
      <div className="mb-4">
        <h6>Preview</h6>
        <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="text-center mb-3">
            <h4 className="text-rpg-primary mb-2">
              MEET YOUR <span style={{ fontWeight: 'bold' }}>
                {content.title || '[TITLE]'}
              </span>
            </h4>
          </div>
          
          <div className="mt-4">
            <h5 className="text-rpg-primary">
              <span style={{ fontWeight: 'bold' }}>
                {content.sectionTitle || 'OUR FOCUS, YOUR DIAGNOSIS.'}
              </span>
            </h5>
            <p className="text-muted small">
              {content.description || '[DESCRIPTION WILL APPEAR HERE]'}
            </p>
          </div>
        </div>
      </div>
    </>
  );

  const renderPage2Editor = () => (
    <>
      <div className="alert alert-info mb-4">
        <div className="d-flex align-items-center">
          <i className="bi bi-info-circle me-2"></i>
          <div>
            <strong>Enabled Sections: {getEnabledSectionsCount()}/5</strong>
            <br />
            <small>Toggle sections on/off to customize which value propositions appear in your PDF. At least one section must be enabled.</small>
          </div>
        </div>
      </div>

      {Object.entries(content.page2Content || {}).map(([sectionKey, sectionData], index) => (
        <div key={sectionKey} className="mb-4">
          <div className={`card ${!sectionData.enabled ? 'border-secondary' : ''}`}>
            <div className={`card-header ${!sectionData.enabled ? 'bg-light text-muted' : ''}`}>
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  {sectionData.enabled ? '✅' : '⭕'} Section {index + 1}
                  {!sectionData.enabled && <span className="badge bg-secondary ms-2">Disabled</span>}
                </h6>
                <div className="form-check form-switch">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id={`enable-${sectionKey}`}
                    checked={sectionData.enabled}
                    onChange={() => handlePage2ToggleEnabled(sectionKey as keyof NonNullable<CustomContent['page2Content']>)}
                  />
                  <label className="form-check-label" htmlFor={`enable-${sectionKey}`}>
                    {sectionData.enabled ? 'Enabled' : 'Disabled'}
                  </label>
                </div>
              </div>
            </div>
            <div className="card-body">
              {/* Title */}
              <div className="mb-3">
                <label className="form-label">
                  <strong>Section Title</strong>
                </label>
                <input
                  type="text"
                  className={`form-control ${!sectionData.enabled ? 'bg-light' : ''}`}
                  value={sectionData.title}
                  onChange={(e) => handlePage2InputChange(
                    sectionKey as keyof NonNullable<CustomContent['page2Content']>, 
                    'title', 
                    e.target.value
                  )}
                  placeholder="Enter section title..."
                  maxLength={100}
                  disabled={!sectionData.enabled}
                />
                <div className="form-text">
                  {sectionData.title?.length || 0}/100 characters
                </div>
              </div>

              {/* Description */}
              <div className="mb-3">
                <label className="form-label">
                  <strong>Section Description</strong>
                </label>
                <textarea
                  className={`form-control ${!sectionData.enabled ? 'bg-light' : ''}`}
                  rows={3}
                  value={sectionData.description}
                  onChange={(e) => handlePage2InputChange(
                    sectionKey as keyof NonNullable<CustomContent['page2Content']>, 
                    'description', 
                    e.target.value
                  )}
                  placeholder="Enter section description..."
                  maxLength={300}
                  disabled={!sectionData.enabled}
                />
                <div className="form-text">
                  {sectionData.description?.length || 0}/300 characters
                </div>
              </div>

              {/* Preview */}
              <div className={`border rounded p-2 ${sectionData.enabled ? '' : 'bg-light'}`} style={{ backgroundColor: sectionData.enabled ? '#f8f9fa' : '#f8f9fa', opacity: sectionData.enabled ? 1 : 0.6 }}>
                <div className="d-flex align-items-start">
                  <span className={`me-2 ${sectionData.enabled ? 'text-success' : 'text-muted'}`} style={{ fontSize: '1.2em' }}>
                    {sectionData.enabled ? '✓' : '⭕'}
                  </span>
                  <div>
                    <h6 className="mb-1" style={{ fontWeight: 'bold' }}>
                      {sectionData.title || '[SECTION TITLE]'}
                      {!sectionData.enabled && <span className="text-muted"> (Hidden in PDF)</span>}
                    </h6>
                    <p className="small text-muted mb-0">
                      {sectionData.description || '[SECTION DESCRIPTION]'}
                    </p>
                  </div>
                </div>
              </div>

              {!sectionData.enabled && (
                <div className="alert alert-warning mt-2 mb-0">
                  <small><i className="bi bi-exclamation-triangle me-1"></i>This section is disabled and will not appear in the PDF.</small>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className="min-vh-100">
      {/* Header */}
      <div className="page-header text-center">
        <div className="container">
          <h1>⚠️ Page Moved</h1>
          <p className="lead mb-0">
            Template editing has been moved to individual service line pages
          </p>
        </div>
      </div>

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            
            <div className="alert alert-warning" role="alert">
              <h4 className="alert-heading">Feature Location Changed!</h4>
              <p>
                The "Edit Template Content" feature has been moved to each individual service line page. 
                This allows you to customize content specifically for Dermatology, Podiatry, and Surgical service lines separately.
              </p>
              <hr />
              <p className="mb-0">
                To edit template content, please go to the service line you want to customize and click the "Edit Template Content" button.
              </p>
            </div>

            <div className="card">
              <div className="card-body">
                <h5 className="card-title">How to Access Template Editing:</h5>
                <ol>
                  <li>Go back to the dashboard</li>
                  <li>Select the service line you want to customize (Dermatology, Podiatry, or Surgical)</li>
                  <li>Click the "✏️ Edit Template Content" button on the service line page</li>
                  <li>Make your changes and save - they will be specific to that service line</li>
                </ol>

                <div className="alert alert-info mt-3">
                  <strong>Benefits of the new system:</strong>
                  <ul className="mb-0">
                    <li>Service line-specific customization</li>
                    <li>Changes are automatically saved and applied</li>
                    <li>No need to navigate away from the pathologist selection page</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center mt-4">
              <button 
                type="button" 
                className="btn rpg-primary me-2"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </button>
              
              <button 
                type="button" 
                className="btn btn-outline-secondary me-2"
                onClick={() => navigate('/service/dermatology')}
              >
                Dermatology
              </button>
              
              <button 
                type="button" 
                className="btn btn-outline-secondary me-2"
                onClick={() => navigate('/service/podiatry')}
              >
                Podiatry
              </button>
              
              <button 
                type="button" 
                className="btn btn-outline-secondary"
                onClick={() => navigate('/service/surgical')}
              >
                Surgical
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPage;